// Blob-download helper — `createObjectURL` + temporary `<a download>`
// anchor click + immediate `revokeObjectURL`. Centralizes the idiom
// shared across the V1 download surfaces.
//
// **Why a helper, not inline.** Originally hoisted at P-034 (D-096)
// at the 3-use threshold across Email JSON + Template JSON + HTML
// download. After the JSON pair drop (P-037.31 §C), the surviving
// consumer is `email-editor-output.tsx` (HTML download, P-033). The
// helper stays — the wrapped idiom is non-trivial (anchor lifecycle +
// URL revoke), the test surface (`download.test.ts`) captures the
// behavior, and any future download surface (V2 PDF receipts /
// archive bundle / etc.) drops in as a one-liner consumer.
//
// **Doesn't cover View-in-Tab.** `openHtmlInTab` (email-editor-
// output.tsx) keeps its own flow because the new tab needs the URL
// alive while it loads — it uses a 60s `setTimeout` cleanup instead
// of immediate revoke. Combining both into one helper would require
// either a callback-shaped contract (callers opt into the timer) or
// two separate exports — choosing the simpler "downloads only" scope
// here keeps the helper surface tight.
//
// **mimeType arg semantics.** When `content` is a Blob, the Blob's
// own `type` is used (mimeType arg ignored; passing it would override
// the existing type which is rarely the desired behavior). When
// `content` is a string, mimeType is the Blob's `type`; falls back
// to `application/octet-stream`.

export function downloadBlob(content: string | Blob, filename: string, mimeType?: string): void {
  const blob =
    content instanceof Blob
      ? content
      : new Blob([content], { type: mimeType ?? 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
